import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { debounceTime, fromEvent, fromEventPattern, map, Observable, shareReplay, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

export type Breakpoint = 'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
export type Orientation = 'portrait' | 'landscape';

/**
 * Service to detect screen size (breakpoints) and device orientation.
 *
 * How it works:
 * - Uses browser's `matchMedia` API to listen for screen changes
 * - Provides both Signals (for templates/computed) and Observables (for RxJS streams)
 * - Shared observables avoid duplicate listeners (better performance)
 *
 * ⚠️ Important - Timing synchronization:
 * - `orientation$` emits immediately on change (no debounce)
 * - `current` signal updates after 100ms debounce (from resize events)
 * - When mixing observables and signals, values may be out of sync
 *
 * To react to multiple properties together:
 * - Use only signals: `effect(() => { const o = orientation(); const c = current(); })`
 * - Or combine observables: `combineLatest([orientation$, toObservable(current)])`
 */
@Injectable({
  providedIn: 'root',
})
export class MibBreakpointService {
  private destroyRef = inject(DestroyRef);

  /**
   * Tailwind CSS breakpoints
   * https://tailwindcss.com/docs/responsive-design
   */
  private breakpoints = {
    sm: '(min-width: 40rem)', // 640px
    md: '(min-width: 48rem)', // 768px
    lg: '(min-width: 64rem)', // 1024px
    xl: '(min-width: 80rem)', // 1280px
    '2xl': '(min-width: 96rem)', // 1536px
    /** custom breakpoints */
    '3xl': '(min-width: 120rem)', // 1920px
  };

  /** Debounced resize events (emits after 100ms debounce) */
  readonly resizeEvents = fromEvent(window, 'resize').pipe(debounceTime(100));

  // ===========
  // Breakpoints
  // ===========

  /** Current active breakpoint: 'base', 'sm', 'md', 'lg', 'xl', '2xl', or '3xl' */
  readonly current = toSignal(this.resizeEvents.pipe(map(() => this.getCurrentBreakpoint())), {
    initialValue: this.getCurrentBreakpoint(),
  });

  /** True when current breakpoint is 'base' or 'sm' (screens smaller than 768px) */
  readonly isBaseOrSm = computed(() => this.current() === 'base' || this.current() === 'sm');

  /** True when current breakpoint is 'lg' or any breakpoint above */
  readonly isLgOrAbove = computed(() => this.current() === 'lg' || this.isXlOrAbove());

  /** True when current breakpoint is 'xl' or any breakpoint above */
  readonly isXlOrAbove = computed(
    () => this.current() === 'xl' || this.current() === '2xl' || this.current() === '3xl',
  );

  /** True when screen width is below 1024px (Tailwind's lg breakpoint) */
  readonly isMaxLg = this.createMediaQuerySignal('(max-width: 63.99rem)');

  // ===========
  // Orientation
  // Note: Observables use shareReplay to avoid creating multiple listeners for the same media query
  // ===========

  private readonly isPortrait$ = this.createMediaQueryObservable('(orientation: portrait)').pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly isLandscape$ = this.createMediaQueryObservable('(orientation: landscape)').pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** True when device is in portrait mode */
  readonly isPortrait = toSignal(this.isPortrait$);

  /** True when device is in landscape mode */
  readonly isLandscape = toSignal(this.isLandscape$);

  /** Observable that emits 'portrait' or 'landscape' when orientation changes */
  readonly orientation$: Observable<Orientation> = this.isPortrait$.pipe(
    map((isPortrait) => (isPortrait ? 'portrait' : 'landscape')),
  );

  /** Current orientation: 'portrait' or 'landscape' */
  readonly orientation = toSignal(this.orientation$, {
    initialValue: window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape',
  });

  /** True when device is in landscape mode AND screen is smaller than 1024px */
  readonly isMobileLandscape = computed(() => this.isMaxLg() && this.isLandscape());

  private getCurrentBreakpoint(): Breakpoint {
    if (typeof window === 'undefined') return 'base';
    if (window.matchMedia(this.breakpoints['3xl']).matches) return '3xl';
    if (window.matchMedia(this.breakpoints['2xl']).matches) return '2xl';
    if (window.matchMedia(this.breakpoints['xl']).matches) return 'xl';
    if (window.matchMedia(this.breakpoints['lg']).matches) return 'lg';
    if (window.matchMedia(this.breakpoints['md']).matches) return 'md';
    if (window.matchMedia(this.breakpoints['sm']).matches) return 'sm';
    return 'base';
  }

  /**
   * Creates a Signal from a media query.
   * Emits only when the query changes (not on every window resize).
   */
  private createMediaQuerySignal(query: string): Signal<boolean> {
    return toSignal(this.createMediaQueryObservable(query), { requireSync: true });
  }

  /**
   * Creates an Observable from a media query.
   * Listens to browser's matchMedia 'change' event for better performance than resize events.
   * Emits the current value immediately on subscription.
   */
  private createMediaQueryObservable(query: string): Observable<boolean> {
    const mediaQuery = window.matchMedia(query);
    return fromEventPattern<MediaQueryListEvent>(
      (handler) => mediaQuery.addEventListener('change', handler),
      (handler) => mediaQuery.removeEventListener('change', handler),
    ).pipe(
      map((event) => event.matches),
      startWith(mediaQuery.matches), // Emit current value immediately
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
